import { useCallback, useEffect, useState } from 'react';

/** How long the reminder stays up before the buttons shrink back. */
export const FEEDBACK_REMINDER_DURATION_MS = 20_000;
/** Length of the shrink back. Keep in sync with `duration-1000` in MessageFeedbackButtons. */
export const FEEDBACK_REMINDER_EXIT_MS = 1000;

/** `on`: enlarged, pulsing buttons and the banner. `leaving`: shrinking back to normal. */
export type FeedbackReminderPhase = 'off' | 'on' | 'leaving';

// A response asks once, so remounting it or coming back to the chat doesn't ask again.
const remindedMessageIds = new Set<string>();

/** Runs the feedback reminder for a response while `isRequested` holds. */
export const useFeedbackReminder = (messageId: string, isRequested: boolean) => {
  const [phase, setPhase] = useState<FeedbackReminderPhase>('off');

  useEffect(() => {
    if (!isRequested || remindedMessageIds.has(messageId)) return;
    remindedMessageIds.add(messageId);

    // A frame later, so the buttons grow from their normal size.
    let hasStarted = false;
    const frame = requestAnimationFrame(() => {
      hasStarted = true;
      setPhase('on');
    });

    return () => {
      if (hasStarted) return;
      cancelAnimationFrame(frame);
      remindedMessageIds.delete(messageId);
    };
  }, [isRequested, messageId]);

  useEffect(() => {
    if (phase === 'off') return;

    const timer =
      phase === 'on'
        ? setTimeout(() => setPhase('leaving'), FEEDBACK_REMINDER_DURATION_MS)
        : setTimeout(() => setPhase('off'), FEEDBACK_REMINDER_EXIT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const dismiss = useCallback(() => {
    setPhase((current) => (current === 'on' ? 'leaving' : current));
  }, []);

  return { phase: isRequested ? phase : 'off', dismiss } as const;
};
